import Cookies from "js-cookie";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import RemoveMarkdown from "remove-markdown";
import { classes, style } from "typestyle";
import { ReactComponent as IconLocation } from "../../assets/icons/location.svg";
import BackendRequest from "../../components/BackendRequest";
import CheckoutLockedReservationModal from "../../components/CheckoutLockedReservationModal";
import DraftHeader from "../../components/DraftHeader";
import Heading from "../../components/Heading";
import {
  LazyImageRenderer,
  BadgeGroup,
  Badge,
  FormattedDateBadges,
  stringToDateFormat,
  GenericText,
  TicketBoxSkeleton,
  LazyImageRendererSkeleton,
  GenericTextSkeleton,
  BadgeSkeleton,
} from "@hooplab/ui-components";
import PracticalInformationModal from "../../components/PracticalInformationModal";
import PromoAlert from "../../components/PromoAlert";
import TicketBox from "../../components/TicketBox";
import { fetchAvailableTicketTypes } from "../../dao/availableTicketTypeGroups";
import { fetchEvent } from "../../dao/event";
import { fetchReservation } from "../../dao/reservation";
import { useQueryParamsLowercase } from "../../hooks/useQueryParams";
import availableTicketTypesReducer, {
  AvailableTicketTypesReducerAction,
  PromoStatus,
} from "../../reducers/availableTicketTypes";
import cartReducer from "../../reducers/cart";
import colors from "../../styles/colors";
import mediaQueries, { upFrom, upTo } from "../../styles/mediaQueries";
import { mb3 } from "../../styles/spacing";
import { Organization } from "../../types";
import ErrorPage from "../ErrorPage";
import CategoryBadge from "./CategoryBadge";
import Description, { DescriptionSkeleton } from "./Description";
import { normalizeCallbackError } from "./errors";
import * as styles from "./styles";
import { pageView } from "../../analytics";
import { useReservationContext } from "../../hooks/useReservationExpiry";
import MediaQuery, { useMediaQuery } from "react-responsive";
import { useCurrentLangContext } from "../../hooks/useCurrentLang";

const fetchTicketTypes = (
  eventId: string,
  promoCodes: string[] | null,
  priority: number[],
  dispatch: React.Dispatch<AvailableTicketTypesReducerAction>,
) => {
  if (promoCodes && promoCodes.length > 0) {
    dispatch({ type: "start_adding_promo_code" });

    fetchAvailableTicketTypes(eventId, promoCodes).then((x) => {
      if (x.status === "fetched") {
        dispatch({
          type: "complete_fetching_available_ticket_types_with_promo",
          groupedTicketTypes: x.entity.groupedTicketTypes,
          priority: priority,
        });
      } else if (x.status === "error") {
        dispatch({ type: "error_fetching_available_ticket_types", error: x.error.type });
      }
    });
  } else {
    fetchAvailableTicketTypes(eventId, []).then((x) => {
      if (x.status === "fetched") {
        dispatch({
          type: "complete_fetching_available_ticket_types",
          groupedTicketTypes: x.entity.groupedTicketTypes,
          priority: priority,
        });
      } else if (x.status === "error") {
        dispatch({ type: "error_fetching_available_ticket_types", error: x.error.type });
      }
    });
  }
};

type RouterParams = { eventId: string };
type EventViewProps = {
  organization: Organization;
};
const EventView: React.FC<EventViewProps> = ({ organization }) => {
  const { eventId } = useParams<RouterParams>();
  const params = useQueryParamsLowercase();
  const callbackMessage = params.get("message");

  const navigate = useNavigate();
  const intl = useIntl();

  const [promoCodes, setPromoCodes] = React.useState<string[]>([]);
  const [cart, cartReducerDispatch] = React.useReducer(cartReducer, {});
  const [practicalInfoModalOpen, setPracticalInfoModalOpen] = React.useState(false);
  const [ticketTypesReducer, ticketTypesDispatch] = React.useReducer(availableTicketTypesReducer, {
    requestState: "fetching",
    groupedTicketTypes: [],
  });
  const [reservationCheckoutLock, setReservationCheckoutLock] = React.useState(false);
  const [promo, setPromo] = React.useState<string | null>(null);
  const [promoStatus, setPromoStatus] = React.useState(PromoStatus.NotSet);

  const priority = params.getAll("p").map((t) => parseInt(t));

  const addParams = (paramList: string[], param: string) => {
    params.delete(param);
    for (let p of paramList.filter((c, index) => {
      return paramList.indexOf(c) === index;
    })) {
      params.append(param, p);
    }
    navigate({ pathname: "", search: params.toString() }, { replace: true });
  };

  const { provided } = useReservationContext();
  const { setReservationExpiry } = provided;
  const upFromDesktopSmall = useMediaQuery(upFrom(mediaQueries.desktopSmall));
  const upToDesktopSmall = useMediaQuery(upTo(mediaQueries.desktopSmall));

  const {
    provided: {
      currentLocale: { name: lang },
    },
  } = useCurrentLangContext()!;

  React.useEffect(() => {
    // This listens to changes to the promo-field and triggers a fetch if given promo is not already in use
    if (promo && promoStatus === PromoStatus.NotSet) {
      if (promoCodes.filter((p) => p.toLowerCase() === promo.toLowerCase()).length > 0) {
        setPromoStatus(PromoStatus.SuccessfulPromo);
      } else {
        setPromoStatus(PromoStatus.Verifying);
        const promos = promoCodes
          ? [...promoCodes.map((t) => t.toLowerCase()), promo.toLowerCase()]
          : [promo.toLowerCase()];
        fetchTicketTypes(eventId || "", promos, priority, ticketTypesDispatch);
      }
    }
  }, [eventId, promo, promoCodes, promoStatus, priority]);

  React.useEffect(() => {
    // This listens to the ticketype-reducer for new fetches and filters out invalid promocodes, while verifying the given promo
    if (ticketTypesReducer.requestState === "fetched" && promoStatus === PromoStatus.Verifying) {
      const secretTts = ticketTypesReducer.groupedTicketTypes
        .flatMap((group) => [
          ...group.ticketTypes
            .filter((tt) => tt.ticketType.isSecret)
            .map((tt) => tt.ticketType.secretKey?.toLowerCase() || ""),
        ])
        .filter((secret) => secret !== ""); // Filter out any empty strings to only have valid secrets

      if (promo) {
        if (secretTts.filter((t) => t.toLowerCase() === promo.toLowerCase()).length > 0) {
          setPromoStatus(PromoStatus.SuccessfulPromo);
        } else {
          setPromoStatus(PromoStatus.WrongfulPromo);
        }
      } else {
        setPromoStatus(PromoStatus.NotSet);
      }
      setPromoCodes(secretTts);
    }
  }, [ticketTypesReducer, promoStatus, promo]);

  React.useEffect(() => {
    // Listen to promoCodes, add to params if promos are accepted. Disable exhaustive-deps due to it wanting a dep for addParams causing an infinite loop
    if (promoCodes && promoCodes.length > 0) {
      addParams(promoCodes, "promo");
    }
    // eslint-disable-next-line
  }, [promoCodes]);

  React.useEffect(() => {
    // Initial fetch, await tickettype-fetch until both urlparams and reservation promos are considered.
    const hasReservationCookie = !!Cookies.get(`r-${eventId || ""}`);
    const urlPromos = params.getAll("promo").map((t) => t.toLowerCase());
    if (urlPromos.length > 0) {
      setPromoStatus(PromoStatus.Verifying);
    }
    if (hasReservationCookie) {
      fetchReservation({ eventId: eventId || "" }).then((r) => {
        if (r.status === "fetched") {
          setReservationCheckoutLock(r.entity.checkoutLock);
          const promos = Array.from(
            new Set([...urlPromos, ...r.entity.promoCodes.map((t) => t.toLowerCase())]),
          );
          if (promos.length > 0) {
            setPromoStatus(PromoStatus.Verifying);
          }
          setReservationExpiry(r.entity.lastTouched);
          cartReducerDispatch({
            type: "set_cart_from_reservation",
            tickets: r.entity.tickets.filter((t) => String(t.eventId) === eventId),
          });
          fetchTicketTypes(eventId || "", promos, priority, ticketTypesDispatch);
        } else {
          fetchTicketTypes(eventId || "", urlPromos, priority, ticketTypesDispatch);
        }
      });
    } else {
      fetchTicketTypes(eventId || "", urlPromos, priority, ticketTypesDispatch);
    }
    // Disable due to exhaustive-deps wanting a dep for params, causing infinite loop.
    // eslint-disable-next-line
  }, [eventId]);

  React.useEffect(() => pageView(), []);

  return (
    <BackendRequest requestFn={fetchEvent} params={eventId}>
      {{
        loading: () => <EventViewSkeleton />,
        error: (error) => {
          if (error.type === "EVENT_NOT_FOUND") {
            return (
              <div>
                <Heading size="xl2">
                  <FormattedMessage
                    id={"screens.Event.eventNotFound"}
                    defaultMessage={"Event not found."}
                  />
                </Heading>
              </div>
            );
          }
          return <ErrorPage />;
        },
        success: (event) => {
          const ticketBox = (
            <>
              {callbackMessage !== null && (
                <div style={{ marginBottom: 12 }}>
                  <PromoAlert
                    icon="fire"
                    title="Oooops"
                    content={normalizeCallbackError(callbackMessage).errorType}
                  />
                </div>
              )}
              <TicketBox
                priceMode={organization.priceMode}
                images={event.images}
                imgixImage={event.image}
                eventName={event.name}
                start={event.start}
                end={event.end}
                eventAvailability={event.availability}
                eventMax={event.maxPerTicket}
                isCancelled={event.isCancelled}
                eventId={event.eventId}
                eventStart={event.start}
                eventEnd={event.end}
                cart={cart}
                priority={priority}
                promoCodes={promoCodes}
                ticketTypes={ticketTypesReducer.groupedTicketTypes.flatMap((group) =>
                  group.ticketTypes.map((tt) => tt.ticketType),
                )}
                groupedTicketTypes={ticketTypesReducer.groupedTicketTypes}
                promoStatus={promoStatus}
                requestState={ticketTypesReducer.requestState}
                showPromosInput={event.hasPromoCodes}
                updateCart={(ticketType, quantity) =>
                  cartReducerDispatch({ type: "set_ticket_quantity", ticketType, quantity })
                }
                onAddPromo={(promo) => {
                  setPromo(promo);
                }}
                resetPromoStatus={() => {
                  setPromo(null);
                  setPromoStatus(PromoStatus.NotSet);
                }}
              />
            </>
          );

          return (
            <div>
              {event.saleState === "inactive" && <DraftHeader />}
              <MediaQuery {...upTo(mediaQueries.tablet)}>
                <div data-nosnippet>
                  <LazyImageRenderer
                    imgixImage={event.image}
                    croppedImages={event.images}
                    env={window.ENVIRONMENT.__IMGIX_ENV__}
                    base={{
                      width: mediaQueries.tablet.maxWidth,
                      ar: "SixteenByNine",
                    }}
                    style={{ width: "100%", display: "block", aspectRatio: "16/9" }}
                    queries={[]}
                    alt={intl.formatMessage(
                      { id: "components.LazyImageRenderer.imageFor", defaultMessage: "Image for" },
                      { image: event.name },
                    )}
                  />
                </div>
              </MediaQuery>

              <div className={styles.container}>
                <div className={styles.hero}>
                  <div className={classes(styles.eventInfoBox)}>
                    <div className={style(mb3)}>
                      <GenericText
                        fontSize="2rem"
                        text={
                          <Heading
                            color={"#191919"}
                            size={
                              upToDesktopSmall ||
                              event.name.split(" ").sort(function (a, b) {
                                return b.length - a.length;
                              })[0].length > 17
                                ? "xl3"
                                : "xl4"
                            }
                          >
                            {event.name}
                          </Heading>
                        }
                      />
                      {event.subtitle && (
                        <Heading size={upToDesktopSmall ? "xl2" : "xl2"}>{event.subtitle}</Heading>
                      )}
                    </div>
                    {event.ingress && <div className={styles.ingress}>{event.ingress}</div>}
                    <div className={styles.badges}>
                      <BadgeGroup>
                        {event.location && (
                          <Badge
                            icon={
                              <IconLocation
                                width={14}
                                height={14}
                                color={colors.grey[400]}
                                className={style({ minWidth: 14 })}
                              />
                            }
                          >
                            {event.location}
                          </Badge>
                        )}
                        <CategoryBadge category={event.category} />
                        {event.dateFormat && event.dateFormat.dateType !== "dont_show" && (
                          <FormattedDateBadges
                            locale={lang}
                            timeZoneAdjustedStart={event.start}
                            timeZoneAdjustedEnd={event.end}
                            dateFormat={stringToDateFormat(event.dateFormat.dateType)}
                            text={event.dateFormat.dateText}
                          />
                        )}
                        {!event.dateFormat && (
                          <FormattedDateBadges
                            locale={lang}
                            timeZoneAdjustedStart={event.start}
                            timeZoneAdjustedEnd={event.end}
                            dateFormat={stringToDateFormat("start_date_and_time")}
                            text={""}
                          />
                        )}
                      </BadgeGroup>
                    </div>
                    {upFromDesktopSmall && (
                      <Description
                        setPracticalInfoModalOpen={() => setPracticalInfoModalOpen(true)}
                        text={
                          organization.mdDescription
                            ? event.description
                            : RemoveMarkdown(event.description)
                        }
                        maxHeight={350}
                        event={event}
                        organization={organization}
                      />
                    )}
                  </div>

                  <div className={classes(styles.ticketBox)}>{ticketBox}</div>

                  {!upFromDesktopSmall && (
                    <Description
                      setPracticalInfoModalOpen={() => setPracticalInfoModalOpen(true)}
                      text={
                        organization.mdDescription
                          ? event.description
                          : RemoveMarkdown(event.description)
                      }
                      maxHeight={200}
                      event={event}
                      organization={organization}
                    />
                  )}
                </div>
              </div>
              {practicalInfoModalOpen && (
                <PracticalInformationModal
                  event={event}
                  open={practicalInfoModalOpen}
                  close={() => setPracticalInfoModalOpen(false)}
                />
              )}
              {reservationCheckoutLock && (
                <CheckoutLockedReservationModal
                  eventId={event.eventId}
                  canComplete
                  open={reservationCheckoutLock}
                  close={() => setReservationCheckoutLock(false)}
                />
              )}
            </div>
          );
        },
      }}
    </BackendRequest>
  );
};

export const EventViewSkeleton: React.FC = () => {
  const upFromDesktopSmall = useMediaQuery(upFrom(mediaQueries.desktopSmall));
  return (
    <div>
      <MediaQuery {...upTo(mediaQueries.tablet)}>
        <LazyImageRendererSkeleton
          base={{
            width: mediaQueries.tablet.maxWidth,
            ar: "SixteenByNine",
          }}
          style={{
            width: "100%",
            display: "block",
            aspectRatio: "16/9",
            height: "calc(100vw*9/16)",
          }}
        />
      </MediaQuery>

      <div className={styles.container}>
        <div className={styles.hero}>
          <div className={"w-full"}>
            <div className={"mb-3"}>
              <GenericTextSkeleton fontSize="2rem" />
              <GenericTextSkeleton fontSize="2rem" />
            </div>
            <div className={"mb-5"}>
              <BadgeGroup>
                <BadgeSkeleton />
                <BadgeSkeleton />
                <BadgeSkeleton />
                <BadgeSkeleton />
              </BadgeGroup>
            </div>
            {upFromDesktopSmall && <DescriptionSkeleton maxHeight={350} />}
          </div>

          <div className={classes(styles.ticketBox)}>
            <TicketBoxSkeleton />
          </div>

          {!upFromDesktopSmall && <DescriptionSkeleton maxHeight={200} />}
        </div>
      </div>
    </div>
  );
};

export default EventView;
